if (!customElements.get('sitemap-blog-discovery')) {
  const fetchXml = async (url) => {
    const response = await fetch(url, { headers: { Accept: 'application/xml' } });

    if (!response.ok) throw new Error(`Request failed: ${url}`);

    return new DOMParser().parseFromString(await response.text(), 'application/xml');
  };

  const samePathname = (url) => new URL(url, window.location.origin).pathname;

  class SitemapBlogDiscovery extends HTMLElement {
    connectedCallback() {
      this.group = document.getElementById('sitemap-blogs');
      this.subgroups = this.group?.querySelector('.sitemap__subgroups');
      this.knownHandles = (this.dataset.known || '').split(',').filter(Boolean);
      this.articlesLimit = parseInt(this.dataset.limit, 10) || 20;

      if (this.subgroups) this.render();
    }

    async render() {
      let handles = [];

      try {
        handles = await this.findMissingBlogHandles();
      } catch (error) {
        return;
      }

      const fragment = document.createDocumentFragment();

      for (const handle of handles) {
        const subgroup = await this.buildSubgroup(handle);

        if (subgroup) fragment.append(subgroup);
      }

      if (!fragment.childElementCount) return;

      this.subgroups.append(fragment);
      this.group.hidden = false;
      this.addNavChip();
    }

    async findMissingBlogHandles() {
      const index = await fetchXml('/sitemap.xml');
      const files = Array.from(index.querySelectorAll('sitemap > loc'))
        .map((node) => samePathname(node.textContent.trim()))
        .filter((path) => path.includes('sitemap_blogs'));
      const handles = new Set();

      for (const file of files) {
        const document = await fetchXml(file);

        document.querySelectorAll('url > loc').forEach((node) => {
          const [, handle] = node.textContent.match(/\/blogs\/([^/?#\s]+)/) || [];

          if (handle && !this.knownHandles.includes(handle)) handles.add(handle);
        });
      }

      return Array.from(handles);
    }

    async buildSubgroup(handle) {
      let feed;

      try {
        feed = await fetchXml(`/blogs/${encodeURIComponent(handle)}.atom`);
      } catch (error) {
        return null;
      }

      const entries = Array.from(feed.querySelectorAll('entry')).slice(0, this.articlesLimit);

      if (!entries.length) return null;

      const list = document.createElement('ul');
      list.className = 'sitemap__links';
      list.setAttribute('role', 'list');

      entries.forEach((entry) => {
        const item = this.buildArticleItem(entry);

        if (item) list.append(item);
      });

      if (!list.childElementCount) return null;

      const subgroup = document.createElement('div');
      subgroup.className = 'sitemap__subgroup';
      subgroup.append(this.buildSubgroupTitle(handle, feed), list);

      return subgroup;
    }

    buildSubgroupTitle(handle, feed) {
      const shopPrefix = `${this.dataset.shop} - `;
      const feedTitle = feed.querySelector('feed > title')?.textContent.trim() || handle;

      const link = document.createElement('a');
      link.className = 'sitemap__subgroup-link';
      link.href = `/blogs/${handle}`;
      link.textContent = feedTitle.startsWith(shopPrefix) ? feedTitle.slice(shopPrefix.length) : feedTitle;

      const title = document.createElement('h4');
      title.className = 'sitemap__subgroup-title h6';
      title.append(link);

      return title;
    }

    buildArticleItem(entry) {
      const href =
        entry.querySelector('link[rel="alternate"]')?.getAttribute('href') ||
        entry.querySelector('link')?.getAttribute('href');
      const title = entry.querySelector('title')?.textContent.trim();

      if (!href || !title) return null;

      const text = document.createElement('span');
      text.className = 'sitemap__link-text';
      text.textContent = title;

      const link = document.createElement('a');
      link.className = 'sitemap__link';
      link.href = samePathname(href);
      link.title = title;
      link.append(text);

      const time = this.buildArticleDate(entry);

      if (time) link.append(time);

      const item = document.createElement('li');
      item.className = 'sitemap__item sitemap__item--article';
      item.setAttribute('role', 'listitem');
      item.append(link);

      return item;
    }

    buildArticleDate(entry) {
      const published =
        entry.querySelector('published')?.textContent || entry.querySelector('updated')?.textContent;
      const date = published ? new Date(published) : null;

      if (!date || Number.isNaN(date.valueOf())) return null;

      const time = document.createElement('time');
      time.className = 'sitemap__meta';
      time.dateTime = date.toISOString().slice(0, 10);
      time.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date);

      return time;
    }

    addNavChip() {
      const chips = document.querySelector('.sitemap__chips');

      if (!chips || chips.querySelector('a[href="#sitemap-blogs"]')) return;

      const chip = document.createElement('a');
      chip.className = 'sitemap__chip';
      chip.href = '#sitemap-blogs';
      chip.textContent = this.dataset.heading;

      const item = document.createElement('li');
      item.setAttribute('role', 'listitem');
      item.append(chip);

      chips.append(item);
    }
  }

  customElements.define('sitemap-blog-discovery', SitemapBlogDiscovery);
}
